export type ExperienceSurfaceFrame = 'plain' | 'dossier';

/** Type sizes in CSS pixels for the glasses display, following Meta's type ramp. */
export interface ExperienceGlassesTypeScale {
  h1: number;
  h2: number;
  body: number;
  small: number;
  meta: number;
}

export interface ExperienceGlassesTokens {
  typePx: ExperienceGlassesTypeScale;
  highlight: 'filled' | 'bracketed';
  treatment: 'none' | 'scanlines';
  glowPx: number;
}

const GLASSES_TYPE_PX: ExperienceGlassesTypeScale = {
  h1: 28,
  h2: 22,
  body: 16,
  small: 14,
  meta: 14,
};

export interface ExperienceTheme {
  key: string;
  displayName: string;
  colors: {
    background: string;
    panel: string;
    text: string;
    muted: string;
    border: string;
    primary: string;
    primaryText: string;
    control: string;
    inputBackground: string;
    inputBorder: string;
    signal: string;
    media: string;
    error: string;
    disabled: string;
  };
  typography: {
    body: string;
    display: string;
    mono: string;
  };
  radius: {
    sm: string;
    md: string;
    lg: string;
    pill: string;
  };
  shadow: {
    sm: string;
    md: string;
  };
  surface: {
    frame: ExperienceSurfaceFrame;
    sideTabLabel: string | null;
  };
  glasses: ExperienceGlassesTokens;
}

type ThemeKey = 'neutral' | 'mission-unpossible' | 'wylding-woods' | 'mu-agency' | 'mu-terminal';

const NEUTRAL_THEME: ExperienceTheme = {
  key: 'neutral',
  displayName: 'Neutral Experience',
  colors: {
    background: '#f7f7f4',
    panel: '#ffffff',
    text: '#1f2428',
    muted: '#687076',
    border: '#deded8',
    primary: '#2f5f55',
    primaryText: '#ffffff',
    control: '#f0f1ed',
    inputBackground: '#ffffff',
    inputBorder: '#d8d8d2',
    signal: '#8f4f46',
    media: '#9a7a3f',
    error: '#b23b32',
    // 3.6:1 on background — the previous #8d969c sat at 2.81, under the 3:1
    // floor the theme validator enforces.
    disabled: '#7a838a',
  },
  typography: {
    body: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    display: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  radius: {
    sm: '8px',
    md: '12px',
    lg: '18px',
    pill: '999px',
  },
  shadow: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.08)',
    md: '0 2px 12px rgba(0, 0, 0, 0.12)',
  },
  surface: {
    frame: 'plain',
    sideTabLabel: null,
  },
  glasses: {
    typePx: GLASSES_TYPE_PX,
    highlight: 'bracketed',
    treatment: 'none',
    glowPx: 0,
  },
};

const MISSION_UNPOSSIBLE_THEME: ExperienceTheme = {
  ...NEUTRAL_THEME,
  key: 'mission-unpossible',
  displayName: 'Mission Unpossible',
  colors: {
    ...NEUTRAL_THEME.colors,
    background: '#05070b',
    panel: '#0d121c',
    text: '#e9f7ee',
    muted: '#8aa39b',
    border: '#233729',
    primary: '#36f36f',
    primaryText: '#041109',
    control: '#111b2a',
    inputBackground: '#070b12',
    inputBorder: '#284334',
    signal: '#b06cff',
    media: '#4bd6ff',
    error: '#ff5c7a',
    disabled: '#52635d',
  },
  typography: {
    ...NEUTRAL_THEME.typography,
    display: '"Courier New", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  radius: {
    sm: '6px',
    md: '10px',
    lg: '16px',
    pill: '999px',
  },
  shadow: {
    sm: '0 0 0 1px rgba(54, 243, 111, 0.16)',
    md: '0 18px 40px rgba(0, 0, 0, 0.45)',
  },
  surface: {
    frame: 'dossier',
    sideTabLabel: 'SIGNAL',
  },
};

const WYLDING_WOODS_THEME: ExperienceTheme = {
  ...NEUTRAL_THEME,
  key: 'wylding-woods',
  displayName: 'Wylding Woods',
  colors: {
    ...NEUTRAL_THEME.colors,
    background: '#f3efe3',
    panel: '#fffaf0',
    text: '#243323',
    // 4.9:1 on background — the previous #6f765d sat at 4.13, under the
    // 4.5:1 AA body-text floor.
    muted: '#636a52',
    border: '#d7cda9',
    primary: '#3f6f3b',
    primaryText: '#fffaf0',
    control: '#e8dec2',
    inputBackground: '#fffdf7',
    inputBorder: '#cbbd91',
    signal: '#9a6a2c',
    media: '#5f8f6b',
    error: '#a44835',
    disabled: '#8e8a76',
  },
  typography: {
    ...NEUTRAL_THEME.typography,
    display: 'Georgia, "Times New Roman", serif',
  },
  surface: {
    frame: 'plain',
    sideTabLabel: null,
  },
};

// The two glasses looks. Both build on Mission Unpossible so the control and
// input tokens stay dark, then override the palette the glasses display needs.
const MU_AGENCY_THEME: ExperienceTheme = {
  ...MISSION_UNPOSSIBLE_THEME,
  key: 'mu-agency',
  displayName: 'Mission Unpossible Agency',
  colors: {
    ...MISSION_UNPOSSIBLE_THEME.colors,
    background: '#000000',
    panel: '#0a171e',
    text: '#F1F4F2',
    muted: '#6C7781',
    border: '#758491',
    primary: '#55D6E8',
    primaryText: '#0A0D10',
    signal: '#E9B949',
    error: '#E05A5A',
  },
  glasses: {
    typePx: GLASSES_TYPE_PX,
    highlight: 'bracketed',
    treatment: 'none',
    glowPx: 6,
  },
};

const MU_TERMINAL_THEME: ExperienceTheme = {
  ...MISSION_UNPOSSIBLE_THEME,
  key: 'mu-terminal',
  displayName: 'Mission Unpossible Terminal',
  colors: {
    ...MISSION_UNPOSSIBLE_THEME.colors,
    background: '#000000',
    panel: '#0d121c',
    text: '#36f36f',
    muted: '#1f9a4f',
    border: '#233729',
    primary: '#36f36f',
    primaryText: '#041109',
    signal: '#6dffa1',
    error: '#c0424a',
  },
  glasses: {
    typePx: GLASSES_TYPE_PX,
    highlight: 'filled',
    treatment: 'scanlines',
    glowPx: 4,
  },
};

const THEMES: Record<ThemeKey, ExperienceTheme> = {
  neutral: NEUTRAL_THEME,
  'mission-unpossible': MISSION_UNPOSSIBLE_THEME,
  'wylding-woods': WYLDING_WOODS_THEME,
  'mu-agency': MU_AGENCY_THEME,
  'mu-terminal': MU_TERMINAL_THEME,
};

export function normalizeThemeKey(themeKey: string | null | undefined): ThemeKey {
  const normalized = (themeKey ?? '').trim().toLowerCase();
  if (normalized === 'mission-unpossible' || normalized === 'mission_unpossible' || normalized === 'mu') {
    return 'mission-unpossible';
  }
  if (normalized === 'mu-agency' || normalized === 'mu_agency') {
    return 'mu-agency';
  }
  if (normalized === 'mu-terminal' || normalized === 'mu_terminal') {
    return 'mu-terminal';
  }
  if (normalized === 'wylding-woods' || normalized === 'wylding_woods' || normalized === 'wylding' || normalized === 'story-stump') {
    return 'wylding-woods';
  }
  return 'neutral';
}

export function resolveExperienceTheme(themeKey: string | null | undefined): ExperienceTheme {
  return THEMES[normalizeThemeKey(themeKey)];
}

// ---------------------------------------------------------------------------
// Theme validation (THEMING.md Resolution Order steps 4-7)
//
// Themes are validated before they are applied. An invalid theme never
// partially applies: the shell falls back to neutral wholesale and records a
// sanitized reason for diagnostics. Security-relevant semantics (error,
// disabled, focus-carrying primary, touch targets) are not themeable escape
// hatches — the contrast floor below is what keeps them legible under any
// authored palette.
// ---------------------------------------------------------------------------

export interface ThemeResolution {
  theme: ExperienceTheme;
  /** Sanitized fallback reason for diagnostics; null when the theme applied. */
  fallbackReason: string | null;
}

const REQUIRED_COLOR_TOKENS: (keyof ExperienceTheme['colors'])[] = [
  'background', 'panel', 'text', 'muted', 'border', 'primary', 'primaryText',
  'control', 'inputBackground', 'inputBorder', 'signal', 'media', 'error',
  'disabled',
];

// [foreground, background, minimum contrast ratio]
// 4.5:1 = WCAG AA body text; 3:1 = AA large/bold text and UI components.
const CONTRAST_CONSTRAINTS: [keyof ExperienceTheme['colors'], keyof ExperienceTheme['colors'], number][] = [
  ['text', 'background', 4.5],
  ['text', 'panel', 4.5],
  ['primaryText', 'primary', 4.5],
  ['muted', 'background', 4.5],
  ['error', 'background', 3],
  ['signal', 'background', 3],
  ['disabled', 'background', 3],
];

function parseHexColor(value: string): [number, number, number] | null {
  const match = value.trim().match(/^#([0-9a-f]{6})$/i);
  if (!match) return null;
  const n = parseInt(match[1], 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(foreground: string, background: string): number | null {
  const fg = parseHexColor(foreground);
  const bg = parseHexColor(background);
  if (!fg || !bg) return null;
  const lighter = Math.max(relativeLuminance(fg), relativeLuminance(bg));
  const darker = Math.min(relativeLuminance(fg), relativeLuminance(bg));
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Validates required tokens and contrast constraints. Returns null when the
 * theme is safe to apply, otherwise a sanitized reason (no raw values beyond
 * token names — reasons surface in diagnostics).
 */
export function validateExperienceTheme(theme: Partial<ExperienceTheme> | null | undefined): string | null {
  if (!theme || typeof theme !== 'object') return 'theme definition missing';
  if (!theme.key) return 'theme key missing';
  if (!theme.colors || typeof theme.colors !== 'object') return 'color tokens missing';

  for (const token of REQUIRED_COLOR_TOKENS) {
    const value = theme.colors[token];
    if (!value) return `color token missing: ${token}`;
    if (!parseHexColor(value)) return `color token invalid: ${token}`;
  }
  if (!theme.typography?.body || !theme.typography?.mono) return 'typography tokens missing';
  if (!theme.radius?.sm || !theme.radius?.pill) return 'radius tokens missing';
  if (!theme.surface?.frame) return 'surface frame missing';

  for (const [fgToken, bgToken, minimum] of CONTRAST_CONSTRAINTS) {
    const ratio = contrastRatio(theme.colors[fgToken]!, theme.colors[bgToken]!);
    if (ratio === null || ratio < minimum) {
      return `contrast below ${minimum}:1 for ${fgToken} on ${bgToken}`;
    }
  }
  return null;
}

/**
 * Resolution Order steps 2-7: resolve the runtime key, validate, apply
 * atomically or fall back wholesale to neutral with a sanitized reason.
 */
export function resolveValidatedExperienceTheme(themeKey: string | null | undefined): ThemeResolution {
  const theme = resolveExperienceTheme(themeKey);
  const problem = validateExperienceTheme(theme);
  if (problem === null) {
    return { theme, fallbackReason: null };
  }
  return { theme: THEMES.neutral, fallbackReason: `${theme.key}: ${problem}` };
}

export function cssVarsForTheme(theme: ExperienceTheme): string {
  return `:root {
  --experience-color-background: ${theme.colors.background};
  --experience-color-panel: ${theme.colors.panel};
  --experience-color-text: ${theme.colors.text};
  --experience-color-muted: ${theme.colors.muted};
  --experience-color-border: ${theme.colors.border};
  --experience-color-primary: ${theme.colors.primary};
  --experience-color-primary-text: ${theme.colors.primaryText};
  --experience-color-control: ${theme.colors.control};
  --experience-color-input-background: ${theme.colors.inputBackground};
  --experience-color-input-border: ${theme.colors.inputBorder};
  --experience-color-signal: ${theme.colors.signal};
  --experience-color-media: ${theme.colors.media};
  --experience-color-error: ${theme.colors.error};
  --experience-color-disabled: ${theme.colors.disabled};
  --experience-font-body: ${theme.typography.body};
  --experience-font-display: ${theme.typography.display};
  --experience-font-mono: ${theme.typography.mono};
  --experience-radius-sm: ${theme.radius.sm};
  --experience-radius-md: ${theme.radius.md};
  --experience-radius-lg: ${theme.radius.lg};
  --experience-radius-pill: ${theme.radius.pill};
  --experience-shadow-sm: ${theme.shadow.sm};
  --experience-shadow-md: ${theme.shadow.md};
  --experience-surface-frame: ${theme.surface.frame};
}`;
}

export function c(token: keyof ExperienceTheme['colors']): string {
  return `var(--experience-color-${kebab(token)})`;
}

export function r(token: keyof ExperienceTheme['radius']): string {
  return `var(--experience-radius-${token})`;
}

export function s(token: keyof ExperienceTheme['shadow']): string {
  return `var(--experience-shadow-${token})`;
}

export const sharedStyles = {
  fontFamily: 'var(--experience-font-body)',
  displayFontFamily: 'var(--experience-font-display)',
  monoFontFamily: 'var(--experience-font-mono)',
} as const;

function kebab(value: string): string {
  return value.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}
